import countries from './data/countries';
import getCookie from './utils/getCookie';
import setCookie, { defaultExpiration } from './utils/setCookie';

/**
 * A custom element for enhancing a static BBB link to a dialog window
 * that shows users their current BBB location and lets them update it.
 *
 * @module iabbb-locale-selector
 */
class LocaleSelector extends HTMLElement {
  constructor() {
    super();

    this.render = () => {
      this.showChangeLocationView = false;
      this.initData();

      // The dialog element is a better choice for this modal, as it has a lot of stuff built in.
      // Positioning it in the top layer relies on anchor positioning, however, which at the time of writing this, has limited browser support.
      // https://caniuse.com/css-anchor-positioning (66% browser support as of September 2024)
      // Once support gets up to 80-90%, we can probably safely use anchor positioning everywhere,
      // and just fall back to a normal link where unsupported.
      // this.useDialogElement = 'anchorName' in document.documentElement.style;
      this.useDialogElement = false;

      if (this.useDialogElement) {
        this.addDialogElement();
      }

      this.replaceLinkWithButton();

      // When anchor positioning is better supported and we strip out this.useDialogElement, this is a good way
      // to have a fallback for users in browsers where it is not well supported.
      // if ('anchorName' in document.documentElement.style) {
      //   this.replaceLinkWithButton();
      // } else {
      //   this.updateLinkIfUserHasNoLocation();
      // }
    };
  }

  connectedCallback() {
    this.render();
  }

  initData = () => {
    const rawCookie = getCookie('iabbb_user_location');
    const userLocationId = rawCookie ? decodeURIComponent(rawCookie) : null;

    if (userLocationId) {
      const userLocationIdParts = decodeURIComponent(userLocationId).split('_');
      const userCountry = userLocationIdParts[userLocationIdParts.length - 1];
      const userState = userLocationIdParts[userLocationIdParts.length - 2];
      const userCity = userLocationIdParts.slice(0, userLocationIdParts.length - 2).join(' ');

      if (userCountry && userState && userCity) {
        this.userLocationName =
          userCountry && userState && userCity ? `${userCity}, ${userState}, ${userCountry}` : null;

        this.userLocationUrl = `/${[
          `${countries.find((x) => x.value === userCountry).shortLabel.toLowerCase()}`,
          `${userState.toLowerCase()}`,
          `${userCity.toLowerCase().replace(' ', '-')}`,
        ].join('/')}`;
      }
    }

    if (window.headerFooterData?.bbb) {
      this.bbbName = window.headerFooterData.bbb.name;
      this.bbbUrl = window.headerFooterData.bbb.url;
    }

    this.hasUserLocation = this.bbbName && this.userLocationName;
  };

  /**
   * Emit a custom event
   * @param {string} name - The event name
   * @param {object} detail - Any details to pass along with the event
   */
  emit = (name, detail = {}) => {
    // Create a new event
    const event = new CustomEvent(name, {
      bubbles: true,
      cancelable: true,
      composed: true,
      detail: detail,
    });

    // Dispatch the event
    return this.dispatchEvent(event);
  };

  addDialogElement = () => {
    const closeIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" aria-hidden="true" fill="currentColor" focusable="false" height="1em" width="1em"><path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3l105.4 105.3c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256l105.3-105.4z"></path></svg>`;
    const visuallyHiddenLabel = `<span class="visually-hidden">close outage disclaimer</span>`;

    const closeButton = document.createElement('button');
    closeButton.className = 'ls-close-button bds-button-unstyled';
    closeButton.innerHTML = `${closeIcon}${visuallyHiddenLabel}`;

    const dialog = document.createElement('dialog');
    dialog.className = 'locale-selector-dialog';
    dialog.tabIndex = -1;

    // 👇 wrapper div aids in light dismiss
    const dialogContent = document.createElement('div');

    const formMethodDialog = document.createElement('form');
    formMethodDialog.method = 'dialog';

    formMethodDialog.appendChild(closeButton);
    dialogContent.appendChild(formMethodDialog);

    dialog.appendChild(dialogContent);

    const screen = document.createElement('div');
    screen.id = 'locale-selector-screen';

    if (this.hasUserLocation) {
      screen.appendChild(this.buildShowLocationDialogContent());
    } else {
      screen.appendChild(this.buildChangeLocationDialogContent());
    }

    dialogContent.appendChild(screen);

    dialog.addEventListener('click', ({ target: dialog }) => {
      if (dialog.nodeName === 'DIALOG') {
        dialog.close('dismiss');
      }
    });

    dialog.addEventListener('close', () => {
      if (this.showChangeLocationView) {
        screen.innerHTML = '';
        screen.appendChild(this.buildShowLocationDialogContent());
        this.showChangeLocationView = false;
      }
    });

    this.appendChild(dialog);
  };

  buildAndOpenLegacyDialog = () => {
    const closeIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" aria-hidden="true" fill="currentColor" focusable="false" height="1em" width="1em"><path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3l105.4 105.3c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256l105.3-105.4z"></path></svg>`;
    const visuallyHiddenLabel = `<span class="visually-hidden">close outage disclaimer</span>`;

    const closeButton = document.createElement('button');
    closeButton.className = 'ls-close-button bds-button-unstyled';
    closeButton.innerHTML = `${closeIcon}${visuallyHiddenLabel}`;

    const dialog = document.createElement('div');
    dialog.role = 'dialog';
    dialog.className = 'locale-selector-dialog';
    dialog.tabIndex = -1;

    // 👇 wrapper div aids in light dismiss
    const dialogContent = document.createElement('div');

    closeButton.onclick = () => {
      this.destroyLegacyDialog();
      this.querySelector('button').focus();
    };

    dialogContent.appendChild(closeButton);

    dialog.appendChild(dialogContent);

    const screen = document.createElement('div');
    screen.id = 'locale-selector-screen';

    if (this.hasUserLocation) {
      screen.appendChild(this.buildShowLocationDialogContent());
    } else {
      screen.appendChild(this.buildChangeLocationDialogContent());
    }

    dialogContent.appendChild(screen);

    this.updateDialogPosition(dialog);

    const dialogBackdrop = document.createElement('div');
    dialogBackdrop.className = 'locale-selector-dialog-backdrop';
    document.body.appendChild(dialogBackdrop);
    dialogBackdrop.addEventListener('click', () => {
      this.destroyLegacyDialog();
    });

    document.body.appendChild(dialog);

    dialog.focus();

    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.destroyLegacyDialog();
        this.querySelector('button').focus();
      }
    });

    window.addEventListener('resize', this.handleLegacyDialogPositionOnResize, { passive: true });
  };

  handleLegacyDialogPositionOnResize = () => {
    const dialogToListen = document.querySelector('.locale-selector-dialog');
    this.updateDialogPosition(dialogToListen);
  };

  destroyLegacyDialog = () => {
    document.querySelector('.locale-selector-dialog-backdrop').remove();
    document.querySelector('.locale-selector-dialog').remove();
    window.removeEventListener('resize', this.handleLegacyDialogPositionOnResize);

    // this.nonDialogElements stores all of the inert elements in memory when the dialog is created
    for (const nonDialogElement of this.nonDialogElements) {
      nonDialogElement.removeAttribute('inert');
    }
  };

  // Use only when we rely solely on native dialog/anchor positioning
  // updateLinkIfUserHasNoLocation = () => {
  //   const link = this.querySelector('a');

  //   if (!this.hasUserLocation) {
  //     link.innerHTML = link.innerHTML.replace('My BBB', 'Set location');
  //   }
  // };

  replaceLinkWithButton = () => {
    const link = this.querySelector('a');

    if (!link) {
      return;
    }

    const btn = document.createElement('button');
    btn.innerHTML = this.hasUserLocation ? link.innerHTML : link.innerHTML.replace('My BBB', 'Set location');
    btn.className = `${link.className} bds-button-unstyled`;
    btn.setAttribute('aria-expanded', 'false');
    btn.onclick = (e) => {
      this.emit(`${this.localName}:open-dialog`, {
        label: e.target.innerText,
      });

      if (this.useDialogElement) {
        this.querySelector('dialog').showModal();
        this.querySelector('dialog').focus();
      } else {
        // stored in memory so that we can remove `inert` when the dialog is closed
        this.nonDialogElements = document.querySelectorAll('body > :not(script)');

        for (const nonDialogElement of this.nonDialogElements) {
          nonDialogElement.setAttribute('inert', 'inert');
        }

        this.buildAndOpenLegacyDialog();
      }
    };
    link.replaceWith(btn);
  };

  updateDialogPosition = (dialog) => {
    const buttonBoundingClientRect = this.querySelector('button').getBoundingClientRect();

    dialog.style.left = `${buttonBoundingClientRect.left}px`;
    dialog.style.top = `${buttonBoundingClientRect.top}px`;
  };

  buildShowLocationDialogContent = () => {
    const linkIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" aria-hidden="true" fill="currentColor" focusable="false" height="1em" width="1em"><path d="M0 256a256 256 0 1 0 512 0 256 256 0 1 0-512 0zm241 121c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l87-87-87-87c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0L345 239c9.4 9.4 9.4 24.6 0 33.9L241 377z"></path></svg>`;

    const locationHeading = document.createElement('h2');
    locationHeading.tabIndex = -1;
    locationHeading.innerText = 'My Home Location';

    const locationLink = document.createElement('a');
    locationLink.className = 'active-underline';
    locationLink.href = this.userLocationUrl;
    locationLink.innerHTML = `${this.userLocationName}${linkIcon}`;
    locationLink.onclick = () => {
      this.emit(`${this.localName}:click-location-link`, {
        location: this.userLocationName,
      });
    };

    const changeLocationButton = document.createElement('button');
    changeLocationButton.className = 'bds-button-unstyled ls-change-home-location';
    changeLocationButton.innerText = 'Change home location';
    changeLocationButton.onclick = () => {
      this.showChangeLocationView = true;
      document.querySelector('#locale-selector-screen').innerHTML = '';
      document.querySelector('#locale-selector-screen').appendChild(this.buildChangeLocationDialogContent());
      document.querySelector('#locale-selector-screen h2').focus();
    };

    const bbbHeading = document.createElement('h2');
    bbbHeading.className = 'my-bbb-heading';
    bbbHeading.innerText = 'My BBB';

    const bbbLink = document.createElement('a');
    bbbLink.className = 'active-underline dtm-locale-selector-location-name';
    bbbLink.href = this.bbbUrl;
    bbbLink.innerHTML = `${this.bbbName}${linkIcon}`;

    const container = document.createElement('div');
    container.appendChild(locationHeading);
    container.appendChild(locationLink);
    container.appendChild(changeLocationButton);
    container.appendChild(bbbHeading);
    container.appendChild(bbbLink);
    return container;
  };

  buildChangeLocationDialogContent = () => {
    const legend = document.createElement('legend');

    const heading = document.createElement('h2');
    heading.tabIndex = -1;
    heading.innerText = 'Set Your Home Location:';

    legend.appendChild(heading);

    const customSelect = document.createElement('iabbb-ls-country-custom-select');
    customSelect.className = 'enhanced-select';

    customSelect.innerHTML = `
      <label class="visually-hidden" for="ls_country">Country</label>
      <select id="ls_country" name="ls_country">
        <option value="USA">United States</option>
        <option value="CAN">Canada</option>
      </select>
    `;

    const nearAutocomplete = document.createElement('iabbb-ls-autocomplete');

    nearAutocomplete.innerHTML = `
      <div class="input-group">
        <label class="dtm-locale-selector-location-pin" for="ls_loc">
          <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 384 512" aria-hidden="true" focusable="false" height="1em" width="1em">
            <path d="M215.7 499.2C267 435 384 279.4 384 192 384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2 12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"></path>
          </svg>
          <span class="visually-hidden">Near</span>
        </label>
        <input id="ls_loc" name="ls_loc" placeholder="city, state or zip" autocomplete="off" autocapitalize="none">
      </div>
    `;
    nearAutocomplete.className = 'autocomplete';

    const button = document.createElement('button');
    button.className = 'bds-button dtm-locale-selector-set-location';
    button.innerText = 'Set Location';

    const fieldset = document.createElement('fieldset');
    fieldset.className = 'change-location';
    fieldset.appendChild(legend);
    fieldset.appendChild(customSelect);
    fieldset.appendChild(nearAutocomplete);
    fieldset.appendChild(button);

    const form = document.createElement('form');

    form.onsubmit = this.onLocationChangeFormSubmit;

    form.appendChild(fieldset);

    return form;
  };

  showLocationError = () => {
    const locationError = 'Location must be a valid city (e.g. Los Angeles, CA) or postal code (e.g. 90210)';
    const warningIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" aria-hidden="true" height="1em" width="1em" fill="currentColor"><path d="M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7.2 40.1S486.3 480 472 480H40c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8.2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24v112c0 13.3 10.7 24 24 24s24-10.7 24-24V184c0-13.3-10.7-24-24-24zm32 224c0-17.7-14.3-32-32-32s-32 14.3-32 32 14.3 32 32 32 32-14.3 32-32z"></path></svg>`;

    const inputId = document.querySelector('iabbb-ls-autocomplete input').id;

    const error = document.createElement('span');
    error.className = 'error';
    error.id = `${inputId}-error`;
    error.innerHTML = `${warningIcon}${locationError}`;

    const autocomplete = document.querySelector('iabbb-ls-autocomplete');
    autocomplete.parentNode.insertBefore(error, autocomplete);

    const autocompleteInput = autocomplete.querySelector('input');

    autocompleteInput.setAttribute('aria-describedby', error.id);
    autocompleteInput.setAttribute('aria-invalid', true);
    autocompleteInput.focus();
  };

  clearLocationError = () => {
    const error = document.querySelector('.error');

    if (error) {
      error.remove();

      const autocomplete = document.querySelector('iabbb-ls-autocomplete');
      const autocompleteInput = autocomplete.querySelector('input');

      autocompleteInput.removeAttribute('aria-describedby');
      autocompleteInput.removeAttribute('aria-invalid');
    }
  };

  onLocationChangeFormSubmit = async (e) => {
    e.preventDefault();

    this.clearLocationError();

    const initialUserLocationId = getCookie('iabbb_user_location');
    const initialUserBbbId = getCookie('iabbb_user_bbb');

    const formValues = Object.fromEntries(new FormData(e.target));
    const selectedCountryName = formValues.ls_country;
    const selectedLocation = formValues.ls_loc;

    const locationId = await fetch(
      `${window.headerFooterData.baseUrl}/api/suggest/location?country=${selectedCountryName}&input=${selectedLocation}&maxMatches=1`,
      { credentials: 'omit' },
    )
      .then((res) => {
        return res.json();
      })
      .then((matches) => {
        return matches.length > 0 ? matches[0].id : null;
      });

    if (locationId) {
      fetch(`${window.headerFooterData.baseUrl}/api/location/bbblocation?locationId=${locationId}`)
        .then((res) => {
          if (!res.ok) {
            throw new Error(`${res.statusText} (status: ${res.status})`);
          }

          return res.json();
        })
        .then((location) => {
          const locationId = `${location.city}_${location.state}_${location.threeLetterIsoCountryCode}`;

          setCookie('iabbb_user_location', locationId, defaultExpiration);
          setCookie('iabbb_user_postalcode', location.postalCode, defaultExpiration);

          if (location.bbbInfo?.bbbId) {
            setCookie('iabbb_user_bbb', location.bbbInfo.bbbId, defaultExpiration);

            window.headerFooterData = {
              ...(window.headerFooterData || {}),
              bbb: {
                name: location.bbbInfo.bbbName,
                url: location.bbbInfo.urls.bbbUrl,
              },
            };

            this.bbbName = window.headerFooterData?.bbb.name;
            this.bbbUrl = window.headerFooterData?.bbb.url;
            this.userLocationUrl = `/${location.country.toLowerCase()}/${location.state.toLowerCase()}/${location.citySeo}`;
            this.userLocationName = `${location.city}, ${location.state}, ${location.threeLetterIsoCountryCode}`;
          }

          const nearInput = document.getElementById('find_loc');

          if (nearInput && !nearInput.value) {
            nearInput.value = location.displayText;
            setCookie('iabbb_find_location', locationId, defaultExpiration);
          }

          const savedCulture = getCookie('iabbb_user_culture');

          const selectedCountry = countries.find((x) => x.value === selectedCountryName);

          if (selectedCountry.culture !== savedCulture) {
            setCookie('iabbb_user_culture', selectedCountry.culture, defaultExpiration);
            setCookie('iabbb_find_location', locationId, defaultExpiration);
          }

          this.emit(`${this.localName}:set-location`, {
            county: selectedCountry.fullLabel,
            culture: selectedCountry.culture,
            location: locationId,
          });

          if (decodeURIComponent(initialUserLocationId) !== locationId) {
            // 👇 Raise event on location change to be consumed by 3rd party/AEM
            this.emit('iabbb:localeselector.setlocation', {
              id: location.id,
              city: location.city,
              state: location.state,
              country: location.country,
              postalCode: location.postalCode,
              latLng: location.latLng,
              bbbInfo: {
                bbbId: location.bbbInfo.bbbId,
                bbbName: location.bbbInfo.bbbName,
                primaryCountry: location.bbbInfo.primaryCountry,
                primaryLanguage: location.bbbInfo.primaryLanguage,
              },
            });

            // Update header/footer links based on new BBB, if the user's BBB changed
            if (initialUserBbbId !== location.bbbInfo.bbbId) {
              fetch(`${window.headerFooterData.baseUrl}/header-footer/api/bbb-data?id=${location.bbbInfo.bbbId}`)
                .then((res) => {
                  if (!res.ok) {
                    throw new Error(`${res.statusText} (status: ${res.status})`);
                  }

                  return res.json();
                })
                .then((location) => {
                  this.emit('hf:set-bbb', location);
                });
            }
          }

          // Update Set Location label
          if (!initialUserLocationId) {
            this.querySelector('button').innerHTML = this.querySelector('button').innerHTML.replace(
              'Set location',
              'My BBB',
            );
          }

          // 👇 switch display
          const screen = document.querySelector('#locale-selector-screen');
          screen.innerHTML = '';
          screen.appendChild(this.buildShowLocationDialogContent());
          this.showChangeLocationView = false;
          document.querySelector('#locale-selector-screen h2').focus();
        })
        .catch((err) => {
          // biome-ignore lint/suspicious/noConsole: for Sentry to suck up
          console.error(err);
        });
    } else {
      this.showLocationError();
    }
  };
}

customElements.define('iabbb-locale-selector', LocaleSelector);
